import { useMemo } from "react";
import { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import { GetAllExpeditionBoResponseDtoExpedition } from "@/types/api/api-types";
import { ActionDataBo } from "@/types/tableAction";
import { userAtom } from "@/stores/authStore";
import { useGetCustoms } from "@/api/customs.api";
import {
  identifierColumn,
  comColumn,
  clientColumn,
  clientDestColumn,
  countryColumn,
  createdAtColumn,
  envoiColumn,
  expeditionRowClassname,
} from "@/components/backOffice/shared/expeditionColumns";
import { Button } from "@/components/ui/button";
import { getFile } from "@/api/s3.api";
import { CustomQuotationTypeBadge } from "@/components/shared/badge/customQuotationType.badge";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import TrashOutlineIcon from "@/assets/icons/trash-outline.svg?react";

type Row = GetAllExpeditionBoResponseDtoExpedition;

type Props = {
  data: Row[];
  rowCount: number;
  pagination: PaginationState;
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setActionData: (actionData: ActionDataBo) => void;
};

export const CustomQuotationsBoxTable = ({
  data,
  rowCount,
  pagination,
  setPagination,
  sorting,
  setSorting,
  setActionData,
}: Props) => {
  const { t } = useTranslation("boQuotations");
  const user = useAtomValue(userAtom);
  const canFinalizeCustomQuotation = user?.role === "SUPERADMIN" || user?.role === "ADMIN";
  const { data: customs = [] } = useGetCustoms();

  const quoteColumn: ColumnDef<Row> = useMemo(
    () => ({
      id: "quote",
      header: t("quotationsBoxTable.columns.quote"),
      size: 160,
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        const quote = row.expeditionDocuments?.documents?.find(
          (doc) => doc.type === FileTypeEnum.EXPEDITION_QUOTE,
        );

        if (!quote) {
          return (
            <span className="px-2 text-sm text-gray-400">
              {t("quotationsBoxTable.quotePending")}
            </span>
          );
        }

        return (
          <Button
            type="button"
            variant="link"
            className="max-w-[150px] truncate px-2 text-sm"
            onClick={async (e) => {
              e.stopPropagation();
              const url = await getFile(quote.path);
              window.open(url, "_blank");
            }}
          >
            {quote.name}
          </Button>
        );
      },
    }),
    [t],
  );

  const customQuotationTypeColumn: ColumnDef<Row> = useMemo(
    () => ({
      id: "customQuotationType",
      header: t("quotationsBoxTable.columns.type"),
      size: 70,
      enableSorting: false,
      cell: (info) => (
        <div className="flex justify-center px-2">
          <CustomQuotationTypeBadge
            type={info.row.original.customQuotation?.type as CustomQuotationTypeEnum | undefined}
          />
        </div>
      ),
    }),
    [t],
  );

  const actionsColumn: ColumnDef<Row> = useMemo(
    () => ({
      id: "actions",
      header: "",
      size: 240,
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-row items-center gap-2 px-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setActionData({ expedition: row, action: "DETAIL" });
              }}
            >
              {t("quotationsBoxTable.details")}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                setActionData({ expedition: row, action: "DELETE" });
              }}
            >
              <TrashOutlineIcon className="h-4 w-4 text-red-500" />
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setActionData({ expedition: row, action: "FINALIZE" });
              }}
              disabled={!canFinalizeCustomQuotation}
            >
              {t("quotationsBoxTable.editQuote")}
            </Button>
          </div>
        );
      },
    }),
    [setActionData, canFinalizeCustomQuotation, t],
  );

  const columns = useMemo(
    () => [
      identifierColumn(setActionData, {
        header: t("quotationsBoxTable.columns.identifier"),
        cotationOnly: true,
      }),
      envoiColumn(),
      clientColumn(),
      clientDestColumn(),
      countryColumn(customs),
      comColumn(setActionData),
      createdAtColumn(),
      quoteColumn,
      customQuotationTypeColumn,
      actionsColumn,
    ],
    [setActionData, quoteColumn, customQuotationTypeColumn, actionsColumn, customs, t],
  );

  return (
    <DataTable
      data={data}
      columns={columns}
      pagination={pagination}
      setPagination={setPagination}
      sorting={sorting}
      setSorting={setSorting}
      rowCount={rowCount}
      autoResetPageIndex={false}
      rowClassname={(row) => expeditionRowClassname(row, "h-18")}
      headerClassName="bg-gray-300"
      headCellClassName="h-12 uppercase text-xs font-semibold tracking-wide text-gray-800 py-2 px-3"
    />
  );
};
